// pages/agents.js — Sessions page.
//
// Owns the `data-page="agents"` section. One row per AI chat session: what it
// is doing (its latest purpose), where (repo, worktree folder, branch), its
// status, the files it holds, when it started and was last active, and its
// spend. Per-row buttons: open the visual rule builder pre-filled with this
// agent_id, or DELETE the registration.
import { state } from "../state.js";
import { agentsState } from "../page-state.js";
import { setBodyHTML, isBodyFirstPaint } from "../dom.js";
import { escapeHTML, ago, parseUTC, formatUSD } from "../util.js";
import { friendlyResource, isPlaceholderPurpose, isAgentCollisionID, chatLabel } from "../format.js";
import { fetchAgents } from "../fetchers.js";
import { isCloudMode } from "../ui.js";

// The daemon's internal owner of worktrees it found on disk — not a session.
const DISCOVERED_SENTINEL_AGENT_ID = "ac:discovered";
// A session with no activity for this long is shown as ended.
const ENDED_AFTER_MS = 2 * 60 * 60 * 1000;
const REFETCH_MS = 15 * 1000;

export async function renderAgentsPage() {
  const body = document.getElementById("agents-body");
  if (!body) return;
  // Only paint the Loading state when the body has no prior content.
  // Without this guard, every 5s refreshLeases tick repainted the
  // Loading state for a frame before the inner render replaced it.
  if (isBodyFirstPaint(body)) {
    setBodyHTML(
      body,
      '<div class="empty" style="opacity:1;animation:none;"><div class="empty-title">Loading sessions…</div></div>',
    );
  }
  try {
    await renderAgentsPageInner(body);
  } catch (err) {
    console.error("[renderAgentsPage] threw:", err);
    setBodyHTML(
      body,
      '<div class="empty" style="opacity:1;animation:none;"><div class="empty-title">Sessions page hit an error</div><div class="empty-detail" style="font-family: var(--font-mono); font-size: 12px;">' +
        escapeHTML((err && err.message) || String(err)) +
        "</div></div>",
    );
  }
}

const baseName = (p) => (p ? String(p).replace(/\/+$/, "").split("/").pop() : "");

// leaseRepo names the repo from a held lease key "file:<repoRoot>:<rel>".
function leaseRepo(leases) {
  for (const l of leases) {
    const m = /^file:([^:]+):/.exec(l.resource || "");
    if (m) return baseName(m[1]);
  }
  return "";
}

function latestTime(...isos) {
  let best = 0;
  let bestISO = "";
  for (const iso of isos) {
    const t = iso ? parseUTC(iso) : NaN;
    if (Number.isFinite(t) && t > best) {
      best = t;
      bestISO = iso;
    }
  }
  return { ms: best, iso: bestISO };
}

// sessionView derives everything one row shows from the agent record plus the
// session state, held leases, worktree and spend the daemon reported.
export function sessionView(a) {
  const id = a.AgentID || "";
  const st = agentsState.states[id];
  const leases = (agentsState.leasesByAgent[id] || [])
    .slice()
    .sort((x, y) => (y.acquired_at || "").localeCompare(x.acquired_at || ""));
  // A placeholder ("auto-inferred from recent activity") is no purpose: the
  // title falls back to the repo instead.
  const stated = (p) => (p && !isPlaceholderPurpose(p) ? p : "");
  const purpose = stated((leases.find((l) => stated(l.purpose)) || {}).purpose) || stated(a.LastPurpose);

  const wt = agentsState.worktreesByAgent[id];
  const repo =
    baseName(a.LastRepoRoot) || baseName(wt && wt.repo_root) || leaseRepo(leases) || (leases.find((l) => l.repo) || {}).repo || "";
  const machine = (agentsState.machineByAgent || {})[id] || "";
  const folder = wt ? baseName(wt.worktree_path) : "";
  const branch = wt ? wt.branch_name || "" : "";

  const last = latestTime(
    a.LastSeenAt,
    st && st.updated_at,
    ...leases.map((l) => l.last_heartbeat_at),
  );
  let status;
  if (st && st.state === "gone") status = "ended";
  else if (!leases.length && last.ms && Date.now() - last.ms > ENDED_AFTER_MS) status = "ended";
  else if (st && st.state === "needs_you") status = "needs_you";
  else if ((st && st.state === "working") || (!st && leases.length)) status = "working";
  else status = "idle";

  const runtime = isAgentCollisionID(id) ? chatLabel(id) : a.Runtime || "unknown";
  const shortID = id.includes("/") ? id.split("/").slice(1).join("/").slice(0, 8) : id.slice(0, 8);
  // The registered display name is usually "<Runtime> <full id>"; the id is
  // already shown, so a name that repeats it is no title.
  const named = a.DisplayName && !a.DisplayName.includes(shortID) ? a.DisplayName : "";
  const title = a.Title || purpose || repo || named || runtime + (machine ? " on " + machine : " session");
  return {
    id,
    shortID,
    runtime,
    enforcement: a.EnforcementLevel || "enforce",
    priority: a.Priority || "",
    title,
    purpose,
    repo,
    folder: folder && folder !== repo ? folder : "",
    branch,
    machine,
    status,
    stateReason: (st && st.reason) || "",
    files: leases.map((l) => friendlyResource(l.resource) || l.resource),
    startedAt: a.CreatedAt || "",
    lastActive: last.iso,
    lastMs: last.ms,
    spend: agentsState.spendByAgent[id],
  };
}

const STATUS_LABEL = {
  working: "Working",
  needs_you: "Needs you",
  idle: "Idle",
  ended: "Ended",
};

function statusTip(v) {
  switch (v.status) {
    case "working":
      return "Running a turn or holding files right now.";
    case "needs_you":
      return "Waiting on a permission or question prompt" + (v.stateReason ? ": " + v.stateReason : ".");
    case "idle":
      return "Waiting at its prompt for the next message.";
    default:
      return "The session closed, or has shown no activity for over 2 hours.";
  }
}

function removeButtonHTML(id) {
  const busy = agentsState.removing.has(id);
  return (
    '<button class="btn btn-sm btn-danger-ghost agents-remove-btn" data-agent-id="' + escapeHTML(id) + '"' +
    (busy ? " disabled" : "") +
    ' data-tooltip="Take this session off the list and free the files it holds">' +
    (busy ? "Removing…" : "Remove") +
    "</button>"
  );
}

function removeErrorHTML(id) {
  const msg = agentsState.removeErrors[id];
  if (!msg) return "";
  return '<div class="sess-remove-error" role="alert">Could not remove this session: ' + escapeHTML(msg) + "</div>";
}

function sessionRowHTML(v) {
  const where = [v.machine, v.repo, v.folder, v.branch && "branch " + v.branch]
    .filter(Boolean)
    .map((x) => escapeHTML(x))
    .join(" · ");
  const open = agentsState.openFiles.has(v.id);
  let files = "";
  if (v.files.length) {
    files =
      '<button type="button" class="sess-files-toggle" data-sess-files="' +
      escapeHTML(v.id) +
      '" aria-expanded="' +
      open +
      '">' +
      (open ? "&#9662; " : "&#9656; ") +
      v.files.length +
      (v.files.length === 1 ? " file held" : " files held") +
      "</button>";
    if (open) {
      files +=
        '<ul class="sess-files">' +
        v.files.map((f) => '<li data-tooltip="' + escapeHTML(f) + '">' + escapeHTML(f) + "</li>").join("") +
        "</ul>";
    }
  } else if (v.status !== "ended") {
    files = '<span class="sess-muted">No files held</span>';
  }
  const spend =
    typeof v.spend === "number"
      ? '<span data-tooltip="AI spend over the last 30 days">' + escapeHTML(formatUSD(v.spend)) + " (30d)</span>"
      : "";
  return (
    '<div class="sess-row sess-' + v.status + '">' +
    '<div class="sess-main">' +
    '<div class="sess-title" data-tooltip="' + escapeHTML(v.title) + '">' + escapeHTML(v.title) +
    (v.priority
      ? ' <span class="sess-prio" data-tooltip="Priority set with agc priority set. Priority rules can let it take files from lower-priority chats.">' +
        escapeHTML(v.priority) +
        "</span>"
      : "") +
    "</div>" +
    '<div class="sess-where">' +
    (where ? where + " · " : "") +
    escapeHTML(v.runtime) +
    ' · <code data-tooltip="' + escapeHTML(v.id) + '">' + escapeHTML(v.shortID) + "</code></div>" +
    (files ? '<div class="sess-files-wrap">' + files + "</div>" : "") +
    "</div>" +
    '<div class="sess-side">' +
    '<span class="sess-status" data-tooltip="' + escapeHTML(statusTip(v)) + '"><span class="sess-dot"></span>' + STATUS_LABEL[v.status] + "</span>" +
    '<div class="sess-times">' +
    '<span data-tooltip="' + escapeHTML(v.lastActive) + '">Active ' + escapeHTML(ago(v.lastActive)) + "</span>" +
    '<span data-tooltip="' + escapeHTML(v.startedAt) + '">Started ' + escapeHTML(ago(v.startedAt)) + "</span>" +
    spend +
    "</div>" +
    '<div class="sess-actions">' +
    '<button class="btn btn-sm btn-primary agents-scope-btn" data-agent-id="' + escapeHTML(v.id) + '" data-tooltip="Make a rule that applies only to this session">+ Rule</button>' +
    removeButtonHTML(v.id) +
    "</div>" +
    removeErrorHTML(v.id) +
    "</div>" +
    "</div>"
  );
}

async function renderAgentsPageInner(body) {
  const stale = !isCloudMode() && Date.now() - agentsState.fetchedAt > REFETCH_MS;
  if ((!agentsState.list.length && !agentsState.error) || stale) {
    await fetchAgents();
  }
  // No fetch has completed yet: still loading, not empty.
  if (!agentsState.fetchedAt && !agentsState.error) return;
  if (agentsState.error) {
    setBodyHTML(
      body,
      '<div class="empty" style="opacity:1;animation:none;"><div class="empty-title">Could not load sessions</div><div class="empty-detail">' +
        escapeHTML(agentsState.error) +
        "</div></div>",
    );
    return;
  }
  const all = agentsState.list
    .filter((a) => a.AgentID !== DISCOVERED_SENTINEL_AGENT_ID)
    .map(sessionView);
  let views = all;
  const only = agentsState.only;
  const banner = document.getElementById("agents-only");
  if (only) views = views.filter((v) => only.ids.has(v.id));
  if (banner) {
    banner.hidden = !only;
    if (only) banner.innerHTML = onlyBannerHTML(only, views.length);
  }
  const q = (agentsState.filter || "").toLowerCase().trim();
  if (q) {
    views = views.filter((v) =>
      [v.id, v.title, v.runtime, v.machine, v.repo, v.folder, v.branch].some((f) => (f || "").toLowerCase().includes(q)),
    );
  }
  const badge = document.getElementById("nav-agents-count");
  if (badge) {
    const live = all.filter((v) => v.status !== "ended").length;
    if (live > 0) {
      badge.textContent = live;
      badge.style.display = "";
    } else {
      badge.style.display = "none";
    }
  }
  if (!views.length) {
    setBodyHTML(
      body,
      '<div class="empty" style="opacity:1;animation:none;">' +
        (q
          ? '<div class="empty-title">No sessions match</div><div class="empty-detail">Try a different search.</div>'
          : '<div class="empty-title">No sessions yet</div>' +
            '<div class="empty-detail">A session appears here when an AI chat starts in a repo with AgentCollision set up. CLI: <code>agc init</code> wires this up.</div>') +
        "</div>",
    );
    return;
  }
  const rank = { working: 0, needs_you: 0, idle: 1, ended: 2 };
  views.sort((x, y) => rank[x.status] - rank[y.status] || y.lastMs - x.lastMs);
  const active = views.filter((v) => v.status !== "ended");
  const ended = views.filter((v) => v.status === "ended");
  let html = '<div class="sess-list">' + active.map(sessionRowHTML).join("");
  if (ended.length) {
    html +=
      '<button type="button" class="sess-ended-toggle" data-sess-ended="1">' +
      (agentsState.showEnded ? "Hide " : "Show ") +
      ended.length +
      (ended.length === 1 ? " ended session" : " ended sessions") +
      "</button>";
    if (agentsState.showEnded) html += ended.map(sessionRowHTML).join("");
  }
  html += "</div>";
  setBodyHTML(body, html);
}

// onlyBannerHTML labels a list opened from a number, and says how many of
// its chats ran on other computers (this page lists this computer's).
export function onlyBannerHTML(only, shown) {
  const away = Math.max(0, only.total - shown);
  return (
    '<div class="ev-query-text"><span class="ev-query-label">Showing</span> <strong>' +
    escapeHTML(only.label) + '</strong> <span class="ev-query-count">' +
    only.total.toLocaleString() + (only.total === 1 ? " chat" : " chats") + "</span>" +
    (away ? '<div class="ev-query-note">' + away.toLocaleString() + " of them ran on your other computers, so " + (away === 1 ? "it isn't" : "they aren't") + " listed here.</div>" : "") +
    '</div><button type="button" class="btn btn-sm" data-agents-only-clear="1">Show all sessions</button>'
  );
}

function onBodyClick(e) {
  const filesBtn = e.target.closest("[data-sess-files]");
  if (filesBtn) {
    const id = filesBtn.dataset.sessFiles;
    if (agentsState.openFiles.has(id)) agentsState.openFiles.delete(id);
    else agentsState.openFiles.add(id);
    renderAgentsPage();
    return;
  }
  if (e.target.closest("[data-sess-ended]")) {
    agentsState.showEnded = !agentsState.showEnded;
    renderAgentsPage();
  }
}

// ---- mount ----

let started = false;
let tickHandler = null;

function start() {
  if (started) return;
  started = true;
  // Initial render — cloud mode has no daemon-driven tick loop, so
  // without this the page sits on its skeleton forever. Mirrors the
  // worktrees / policy / priority / spend / team / analytics fixes.
  renderAgentsPage();
  tickHandler = () => {
    if (state.activePage === "agents") renderAgentsPage();
  };
  document.addEventListener("dashboard:tick", tickHandler);
}

function stop() {
  if (!started) return;
  started = false;
  if (tickHandler) {
    document.removeEventListener("dashboard:tick", tickHandler);
    tickHandler = null;
  }
}

function bindLifecycle() {
  const section = document.querySelector('section.page[data-page="agents"]');
  if (!section) return;
  const body = document.getElementById("agents-body");
  if (body) body.addEventListener("click", onBodyClick);
  const only = document.getElementById("agents-only");
  if (only)
    only.addEventListener("click", (e) => {
      if (!e.target.closest("[data-agents-only-clear]")) return;
      agentsState.only = null;
      renderAgentsPage();
    });
  if (section.classList.contains("active")) start();
  const obs = new MutationObserver((mutations) => {
    mutations.forEach((m) => {
      if (m.attributeName !== "class") return;
      if (section.classList.contains("active")) start();
      else stop();
    });
  });
  obs.observe(section, { attributes: true });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", bindLifecycle);
} else {
  bindLifecycle();
}
